/* COMPLÉTEZ VOTRE COMMANDE — le chargeur des suggestions du panier.
 *
 * Le balisage, lui, est rendu par sections/yumi-cart-upsell.liquid, qu'on
 * demande à l'API de recommandations de Shopify. On passe par cette porte-là
 * parce qu'elle seule donne à la fois les recommandations de Shopify ET nos
 * métachamps `yumi.*` (sur devis, lot minimum) — le JSON de l'autre porte les
 * ignore.
 *
 * ⚠️ NE PAS DÉCLENCHER LE CHARGEMENT SUR UN IntersectionObserver.
 * La première version le faisait, et ne s'affichait jamais : le conteneur part
 * `hidden` (pour ne pas laisser un trou pendant le chargement), donc en
 * `display:none`, et un élément en `display:none` n'est JAMAIS « intersectant ».
 * L'observateur ne se déclenchait pas une seule fois. Mesuré le 25/08/2026.
 *
 * Le déclencheur est donc explicite, et différent selon l'endroit :
 *   - sur /cart, le bloc est visible d'emblée → on charge au premier temps mort ;
 *   - dans le tiroir, on attend qu'il s'ouvre. On surveille l'attribut `open`
 *     du <theme-drawer> plutôt qu'un événement : un attribut ne change pas de
 *     nom d'une version de thème à l'autre.
 *
 * On ne demande rien tant que personne ne regarde : sur une page produit, tant
 * que le tiroir reste fermé, aucune requête ne part. (Chantier vitesse.)
 *
 * Le balisage vient du rendu de section du panier (/cart?section_id=...) :
 * c'est la section qui choisit, d'après le panier lui-même (01/10/2026 ;
 * avant, les « apparentés » de Shopify, trop souvent hors sujet).
 *
 * L'ajout, lui, ne passe pas par ici : chaque carte est un formulaire de classe
 * `yc-add-form`, et layout/theme.liquid intercepte déjà ces formulaires-là.
 */

import { StandardEvents } from '@shopify/events';

class YumiCartUpsell extends HTMLElement {
  #chargement = null;
  #minuteur = null;
  #guetteur = null;
  #veilleur = null;
  #relance = null;

  connectedCallback() {
    /* RECONNECTÉ APRÈS LE MORPH (mesuré le 07/09/2026, panier vidé par la corbeille) :
       le thème redessine le tiroir deux fois ; entre les deux, l'élément est
       retiré puis remis, et ses enfants — les favoris déjà chargés — sont perdus
       pendant l'absence, là où aucun veilleur ne regarde. Au retour, #chargement
       croyait encore avoir fini et #charger ne refaisait rien : « Yumi vous
       propose » restait vide. Reconnecté sans enfant, on repart de zéro (reconnecte). */
    if (this.childElementCount === 0) this.#chargement = null;
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#replanifier);

    /* LE VIDAGE PAR LE MORPH DU THÈME. Quand le panier change, le thème
       refond le tiroir avec le HTML du serveur, où cet élément est vide.
       `data-skip-node-update` lui interdit de toucher l'élément… mais pas ses
       enfants : la liste chargée est effacée. Si cet effacement arrive APRÈS
       la relance de 700 ms, rien ne la refaisait : « Yumi vous suggère »
       restait vide (mesuré au navigateur le 05/09/2026, au retour du panier
       vide). On surveille donc nos propres enfants : vidé sans être caché,
       on recharge, une fois, un peu plus tard. */
    this.#veilleur = new MutationObserver(() => {
      if (this.hidden || this.childElementCount > 0) return;
      /* Le temps du rechargement, on garde la place : sinon la colonne se
         contracte, le contenu au-dessus remonte et l'écran saute. */
      if (this.dataset.ycHauteur) this.style.minHeight = this.dataset.ycHauteur + 'px';
      clearTimeout(this.#relance);
      this.#relance = setTimeout(() => {
        if (this.hidden || this.childElementCount > 0) return;
        this.#chargement = null;
        this.#charger();
      }, 400);
    });
    this.#veilleur.observe(this, { childList: true });

    if (this.dataset.contexte === 'drawer') {
      this.#attendreLeTiroir();
    } else {
      this.#auPremierTempsMort(() => this.#charger());
    }
  }

  disconnectedCallback() {
    clearTimeout(this.#minuteur);
    clearTimeout(this.#relance);
    this.#veilleur?.disconnect();
    this.#guetteur?.disconnect();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#replanifier);
  }

  #auPremierTempsMort(quoi) {
    if (typeof requestIdleCallback === 'function') requestIdleCallback(quoi, { timeout: 1500 });
    else setTimeout(quoi, 200);
  }

  /* Le tiroir vit dans un <theme-drawer> qui porte l'attribut `open` quand il
     est ouvert. On charge à la première ouverture — et tout de suite s'il est
     déjà ouvert (retour de paiement, rechargement de page tiroir ouvert). */
  #attendreLeTiroir() {
    const tiroir = this.closest('theme-drawer');
    if (!tiroir) {
      this.#auPremierTempsMort(() => this.#charger());
      return;
    }

    if (tiroir.hasAttribute('open')) {
      this.#charger();
      return;
    }

    this.#guetteur = new MutationObserver(() => {
      if (tiroir.hasAttribute('open')) {
        this.#guetteur?.disconnect();
        this.#guetteur = null;
        this.#charger();
      }
    });
    this.#guetteur.observe(tiroir, { attributes: true, attributeFilter: ['open'] });
  }

  get #routePanier() {
    return (this.dataset.routePanier || '/cart') + '.js';
  }

  /* Toute mutation du panier peut périmer la liste : le produit suggéré vient
     peut-être d'être ajouté, ou le dernier article a changé. */
  #replanifier = () => {
    clearTimeout(this.#minuteur);
    /* Le temps que Shopify enregistre la ligne : demander trop tôt renverrait
       une liste qui contient encore le produit qu'on vient d'ajouter. */
    this.#minuteur = setTimeout(() => {
      this.#chargement = null;
      this.#charger();
    }, 700);
  };

  #charger() {
    if (this.#chargement) return this.#chargement;

    this.#chargement = (async () => {
      /* Mode « vide » : l'élément n'existe que dans l'état vide du tiroir, on
         n'a pas besoin de relire le panier — et juste après un retrait,
         Shopify le dit encore plein pendant une seconde ou deux, ce qui
         faisait attendre cinq secondes les favoris (mesuré le 06/09/2026). */
      if (this.dataset.mode === 'vide') {
        await this.#chargerVide();
        this.#oublierLeCode();
        return;
      }
      const idProduit = await this.#produitDeReference();
      if (!idProduit) {
        /* Panier vide. En mode « vide » (le tiroir vide, 05/09/2026), on
           demande les trois favoris à la section dédiée ; sinon on s'efface. */
        if (this.dataset.mode === 'vide') await this.#chargerVide();
        else this.#vider();
        return;
      }

      /* UNE seule requête, au rendu de section du panier (01/10/2026). La
         section lit le panier elle-même et choisit les compléments de chacun
         des trois derniers articles (snippets/yc-reco-choix.liquid). Les
         « apparentés » de Shopify ne servent plus : mesurés le 01/10/2026,
         13 % seulement étaient en rapport avec le produit. */
      const balisage = await this.#demander();

      if (this.#aDesCartes(balisage)) {
        this.innerHTML = balisage;
        this.hidden = false;
        this.#retenirHauteur();
        this.#armerFenetre();
      } else {
        this.#vider();
      }
    })().catch(() => {
      this.#vider();
    });

    return this.#chargement;
  }

  /* Le dernier article ajouté est le meilleur point de départ : c'est celui que
     le client a en tête. */
  async #produitDeReference() {
    const reponse = await fetch(this.#routePanier, {
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    });
    if (!reponse.ok) return null;
    const panier = await reponse.json();
    if (!panier.items || !panier.items.length) return null;
    return panier.items[panier.items.length - 1].product_id;
  }

  async #demander() {
    const url = `${this.dataset.routePanier || '/cart'}?section_id=yumi-cart-upsell`;
    const reponse = await fetch(url, { headers: { Accept: 'text/html' }, cache: 'no-store' });
    if (!reponse.ok) return '';
    return await reponse.text();
  }

  /* LE PANIER VIDE. Les trois favoris sont rendus par
     sections/yc-panier-vide-suggestions.liquid, qu'on demande au Section
     Rendering API : ainsi aucune carte produit n'est écrite dans le HTML des
     pages (Google en ferait la vignette ou l'extrait de la page). */
  async #chargerVide() {
    try {
      /* Sur une fiche, la section est demandée à l'adresse de la fiche : elle
         propose alors les compléments du produit regardé (01/10/2026). */
      const surFiche = /^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?products\/[^/]+\/?$/i.test(window.location.pathname);
      const base = surFiche ? window.location.pathname : '/';
      const reponse = await fetch(`${base}?section_id=yc-panier-vide-suggestions`, {
        headers: { Accept: 'text/html' },
      });
      const balisage = reponse.ok ? await reponse.text() : '';
      if (typeof balisage === 'string' && balisage.indexOf('data-yc-vide-rempli') !== -1) {
        this.innerHTML = balisage;
        this.hidden = false;
        this.#retenirHauteur();
        this.#armerFenetre();
        return;
      }
    } catch (e) {
      /* on s'efface, comme pour un panier plein sans suggestion */
    }
    this.#vider();
  }

  /* yc-upsell-fenetre (client, 07/09/2026) : sur téléphone la liste défile à
     l'horizontale ; deux fondus discrets aux bords disent qu'il y a une suite
     à gauche ou à droite. Ici on ne fait que poser les classes ; la feuille
     de style (snippets/yumi-cart-upsell.liquid) dessine les fondus, et
     seulement sous 750 px — sur ordinateur les classes ne changent rien. */
  #armerFenetre() {
    const fenetre = this.querySelector('.yc-upsell__fenetre');
    const liste = fenetre && fenetre.querySelector('.yc-upsell__liste');
    if (!liste) return;
    const poser = () => {
      const reste = liste.scrollWidth - liste.clientWidth - liste.scrollLeft;
      fenetre.classList.toggle('yc-upsell__fenetre--gauche', liste.scrollLeft > 4);
      fenetre.classList.toggle('yc-upsell__fenetre--droite', reste > 4);
    };
    liste.addEventListener('scroll', poser, { passive: true });
    if (typeof ResizeObserver !== 'undefined') new ResizeObserver(poser).observe(liste);
    poser();
  }

  /* La hauteur du bloc plein, retenue pour le veilleur (voir connectedCallback). */
  #retenirHauteur() {
    requestAnimationFrame(() => {
      if (this.childElementCount > 0) this.dataset.ycHauteur = String(this.offsetHeight);
      this.style.minHeight = '';
    });
  }

  /* Un code promo posé sur un panier qu'on a vidé y reste collé : au prochain
     article ajouté, la remise est déjà là, sans que le client ait cliqué.
     Le client veut le clic (06/09/2026 : la gratification du geste). Donc,
     panier vide et code présent : on retire le code. Sans effet sur le
     ticket YUMI5 de l'état vide, qui pose le code par un vrai clic. */
  async #oublierLeCode() {
    try {
      const reponse = await fetch(this.#routePanier, { headers: { Accept: 'application/json' }, cache: 'no-store' });
      if (!reponse.ok) return;
      const panier = await reponse.json();
      if (panier.item_count !== 0 || !(panier.discount_codes || []).length) return;
      await fetch((this.dataset.routePanier || '/cart') + '/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ discount: '' }),
      });
    } catch (e) {
      /* sans gravité : le code sera simplement encore là */
    }
  }

  #aDesCartes(balisage) {
    return typeof balisage === 'string' && balisage.indexOf('data-yc-upsell-rempli') !== -1;
  }

  #vider() {
    this.style.minHeight = '';
    this.innerHTML = '';
    this.hidden = true;
  }
}

/* « DÉCOUVRIR UN PRODUIT » (le tiroir vide, 05/09/2026) : une fiche au hasard,
 * disponible, EN STOCK, à 30 € ou plus. On tire une page du catalogue (48 fiches,
 * `data-yc-pages` pages) et on demande à la section `yc-decouvrir`
 * (sections/yc-decouvrir.liquid) la liste des fiches de cette page qui passent
 * le filtre : c'est elle qui lit le vrai stock — /products.json, lui, dit
 * « disponible » pour une précommande (mesuré le 05/09/2026). Trois essais,
 * sinon le lien mène au catalogue. Écouteur unique sur le document : le tiroir
 * est réinjecté par l'hydratation, un écouteur posé sur le bouton lui-même ne
 * survivrait pas. */
if (!window.__ycDecouvrir) {
  window.__ycDecouvrir = true;
  document.addEventListener('click', async (ev) => {
    if (!(ev.target instanceof Element)) return;
    const lien = ev.target.closest('a[data-yc-decouvrir]');
    if (!lien) return;
    ev.preventDefault();
    const pages = Math.max(1, parseInt(lien.dataset.ycPages || '1', 10) || 1);
    const source = lien.dataset.ycSource || '/collections/all';
    lien.setAttribute('aria-busy', 'true');
    try {
      for (let essai = 0; essai < 3; essai++) {
        const page = 1 + Math.floor(Math.random() * pages);
        const reponse = await fetch(`${source}?section_id=yc-decouvrir&page=${page}`, {
          headers: { Accept: 'text/html' },
        });
        if (!reponse.ok) break;
        const doc = new DOMParser().parseFromString(await reponse.text(), 'text/html');
        const brut = doc.querySelector('script[data-yc-decouvrir-liste]');
        const handles = brut ? JSON.parse(brut.textContent || '[]') : [];
        if (handles.length) {
          window.location.href = `/products/${handles[Math.floor(Math.random() * handles.length)]}`;
          return;
        }
      }
    } catch (e) {
      /* le lien fait le reste */
    }
    window.location.href = lien.href;
  });
}

if (!customElements.get('yumi-cart-upsell')) {
  customElements.define('yumi-cart-upsell', YumiCartUpsell);
}
